/**
 * bundle-tiers.js — Hair Queen LA
 *
 * Bundle quantity selector for a single listing.
 * All tier data ships in the markup, so switching tiers makes no network requests.
 */

class BundleTiers extends HTMLElement {
  connectedCallback() {
    const raw = this.querySelector('[data-bundle-tiers-data]');
    if (!raw) return;

    try {
      this.data = JSON.parse(raw.textContent);
    } catch (e) {
      console.warn('[bundle-tiers] could not parse tier data', e);
      return;
    }

    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.singlePrices = this.normalizePrices(this.data.singlePrices);

    this.ladder = this.querySelector('[data-tier-ladder]');
    this.optionsHost = this.querySelector('[data-tier-options]');
    this.summary = this.querySelector('[data-tier-summary]');

    // A product whose Bundle tiers metafield points at another family does not
    // appear in its own ladder. Falling back to the first tier then made the
    // page impersonate that other product — its photo, its prices and its
    // variant in the cart — while the title still said otherwise.
    this.tier = this.findTier(this.data.currentId);
    this.orphan = !this.tier;
    if (this.orphan) {
      this.tier = this.data.tiers[0];
      console.warn(
        '[bundle-tiers] this product is missing from its own Bundle tiers metafield; ' +
          'leaving the page photo and the add-to-cart form alone'
      );
    }
    this.selection = this.defaultSelection(this.tier);

    this.ladder.addEventListener('click', this.onTierClick.bind(this));
    this.optionsHost.addEventListener('click', this.onOptionClick.bind(this));

    this.render();
  }

  /* ---------- data ---------- */

  // { "12": 10000, ... } — string keys, prices in cents
  normalizePrices(src) {
    if (!src || typeof src !== 'object') return null;
    const out = {};
    Object.keys(src).forEach((k) => {
      const n = parseFloat(k);
      if (!Number.isNaN(n)) out[n] = Number(src[k]);
    });
    return Object.keys(out).length ? out : null;
  }

  findTier(id) {
    return this.data.tiers.find((t) => String(t.id) === String(id));
  }

  // Locate the length and texture options within a given tier
  optionIndexes(tier) {
    const names = (tier.optionNames || []).map((n) => String(n).toLowerCase());
    let size = names.findIndex((n) => /size|length/.test(n));
    const texture = names.findIndex((n) => /texture|type/.test(n));
    if (size === -1) size = 0;
    return { size, texture };
  }

  // "14in 16in 18in" -> [14, 16, 18]
  parseLengths(value) {
    return (String(value).match(/\d+(?:\.\d+)?/g) || []).map(Number);
  }

  defaultSelection(tier) {
    const first = tier.variants.find((v) => v.available) || tier.variants[0];
    return first ? first.options.slice() : [];
  }

  variantFor(tier, options) {
    return tier.variants.find(
      (v) => v.options.length === options.length && v.options.every((o, i) => o === options[i])
    );
  }

  /**
   * Carry the current selection over to another tier.
   * Texture matches exactly, length matches the closest starting inch.
   */
  projectSelection(from, to) {
    const fromIdx = this.optionIndexes(from);
    const toIdx = this.optionIndexes(to);

    const wantTexture = fromIdx.texture > -1 ? this.selection[fromIdx.texture] : null;
    const wantStart = this.parseLengths(this.selection[fromIdx.size])[0];

    const pool = to.variants.filter((v) => {
      if (!v.available) return false;
      if (wantTexture && toIdx.texture > -1) return v.options[toIdx.texture] === wantTexture;
      return true;
    });

    const candidates = pool.length ? pool : to.variants.filter((v) => v.available);
    if (!candidates.length) return this.defaultSelection(to);

    const best = candidates.reduce((acc, v) => {
      const start = this.parseLengths(v.options[toIdx.size])[0];
      const delta = Math.abs((start || 0) - (wantStart || 0));
      return !acc || delta < acc.delta ? { variant: v, delta } : acc;
    }, null);

    return best.variant.options.slice();
  }

  /** Cost of this set of lengths bought one bundle at a time. Null when data is missing. */
  retailFor(tier, options) {
    if (!this.singlePrices) return null;
    const idx = this.optionIndexes(tier);
    const lengths = this.parseLengths(options[idx.size]);
    if (!lengths.length) return null;

    let sum = 0;
    for (const len of lengths) {
      if (this.singlePrices[len] == null) return null; // no price, no savings claim
      sum += this.singlePrices[len];
    }
    return sum;
  }

  /* ---------- events ---------- */

  onTierClick(event) {
    const link = event.target.closest('[data-tier]');
    if (!link) return;

    const next = this.findTier(link.dataset.tier);
    if (!next || next === this.tier) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    this.selection = this.projectSelection(this.tier, next);
    this.tier = next;
    // Picking a row is an explicit choice, so the ladder may drive the page
    // from here on even if the listing it started from was misconfigured.
    this.orphan = false;
    this.render();

    if (window.history && window.history.replaceState) {
      window.history.replaceState({}, '', next.url);
    }
  }

  onOptionClick(event) {
    const btn = event.target.closest('[data-option-index]');
    if (!btn) return;

    const index = Number(btn.dataset.optionIndex);
    const next = this.selection.slice();
    next[index] = btn.dataset.optionValue;

    // Fall back to the nearest available combination if this one does not exist
    if (!this.variantFor(this.tier, next)) {
      const fallback = this.tier.variants.find(
        (v) => v.available && v.options[index] === btn.dataset.optionValue
      );
      if (!fallback) return;
      this.selection = fallback.options.slice();
    } else {
      this.selection = next;
    }

    this.render();
  }

  /* ---------- rendering ---------- */

  render() {
    this.renderLadder();
    this.renderOptions();
    this.renderSummary();

    // While the page is standing on a product that is not in its own ladder,
    // the safe thing is to leave the theme's own photo and form untouched:
    // better a ladder that links elsewhere than a cart holding the wrong item.
    // The first real tier click clears the flag — by then the shopper has
    // chosen a listing from the ladder deliberately.
    if (!this.orphan) {
      this.syncForm();
      this.syncImage();
    }
  }

  renderLadder() {
    this.ladder.querySelectorAll('[data-tier]').forEach((el) => {
      const tier = this.findTier(el.dataset.tier);
      if (!tier) return;

      el.toggleAttribute('aria-current', tier === this.tier);

      const options = tier === this.tier ? this.selection : this.projectSelection(this.tier, tier);
      const variant = this.variantFor(tier, options);
      const retail = this.retailFor(tier, options);

      const now = el.querySelector('[data-tier-now]');
      const was = el.querySelector('[data-tier-was]');
      const save = el.querySelector('[data-tier-save]');

      if (!variant) {
        now.textContent = 'Sold out';
        was.hidden = true;
        save.textContent = '';
        return;
      }

      now.textContent = this.money(variant.price);

      if (retail && retail > variant.price) {
        const diff = retail - variant.price;
        was.hidden = false;
        was.textContent = this.money(retail);
        save.textContent = `Save ${this.money(diff)} · ${Math.round((diff / retail) * 100)}% off`;
        save.classList.remove('is-flat');
      } else {
        was.hidden = true;
        save.textContent = tier.count > 1 ? '' : 'Single bundle price';
        save.classList.add('is-flat');
      }
    });
  }

  /**
   * What this value would cost if the shopper picked it, with the rest of the
   * selection held steady. Falls back to the cheapest variant carrying the
   * value when that exact combination does not exist.
   */
  priceForValue(optionIndex, value) {
    const opts = this.selection.slice();
    opts[optionIndex] = value;

    const exact = this.variantFor(this.tier, opts);
    if (exact) return exact.price;

    const carrying = this.tier.variants.filter((v) => v.options[optionIndex] === value);
    const available = carrying.filter((v) => v.available);
    const pool = available.length ? available : carrying;
    return pool.length ? Math.min(...pool.map((v) => v.price)) : null;
  }

  /**
   * Display-only renaming of an option, e.g. "Size" shown as "Length".
   * The admin keeps its own naming; nothing here touches variant matching.
   */
  optionLabel(name) {
    const map = this.dataset.optionLabels;
    if (!map) return name;
    const wanted = String(name).trim().toLowerCase();
    for (const pair of map.split(',')) {
      const [from, to] = pair.split(':').map((s) => (s || '').trim());
      if (from && to && from.toLowerCase() === wanted) return to;
    }
    return name;
  }

  /**
   * Display-only value formatting. "12in" is how the admin stores it; the
   * storefront shows 12". Handles a set of lengths in one string too.
   * Never used for data-option-value — that has to stay the real value.
   */
  displayValue(value) {
    if (this.dataset.valueFormat !== 'inches') return value;
    return String(value).replace(/(\d+(?:\.\d+)?)\s*in\b/gi, '$1"');
  }

  renderOptions() {
    const idx = this.optionIndexes(this.tier);

    const groups = (this.tier.optionNames || []).map((name, i) => {
      const values = [...new Set(this.tier.variants.map((v) => v.options[i]))];

      // Deltas are measured from the cheapest value in the group, so the
      // entry-level option reads as "no extra" rather than as a discount.
      const prices = values.map((value) => this.priceForValue(i, value));
      const known = prices.filter((p) => p != null);
      const base = known.length ? Math.min(...known) : null;

      const pills = values
        .map((value, vi) => {
          const anyAvailable = this.tier.variants.some((v) => v.options[i] === value && v.available);
          const selected = this.selection[i] === value;
          const diff = base != null && prices[vi] != null ? prices[vi] - base : 0;
          const delta =
            diff > 0 ? `<span class="bundle-pill__delta">+${this.moneyShort(diff)}</span>` : '';

          return `<button type="button" class="bundle-pill"
                    data-option-index="${i}" data-option-value="${this.escape(value)}"
                    aria-pressed="${selected}" ${anyAvailable ? '' : 'disabled'}>
                    <span class="bundle-pill__value">${this.escape(this.displayValue(value))}</span>${delta}
                  </button>`;
        })
        .join('');

      const hint =
        i === idx.size && this.tier.count > 1
          ? '<span class="bundle-tiers__hint">One set — lengths go from shortest to longest</span>'
          : '';

      // The chosen value is repeated in the label, so the current state is
      // readable without scanning the row for the filled pill.
      const chosen = this.selection[i]
        ? `<span class="bundle-tiers__value">${this.escape(this.displayValue(this.selection[i]))}</span>`
        : '';

      // Long value lists scan far better as an even grid than as a ragged row.
      const grid = values.length > 4 ? ' bundle-pills--grid' : '';

      // Column count follows the longest label. A five-bundle set reads
      // "12" 14" 16" 18" 20"" — five times the width of a single length — so a
      // fixed four-across pushed it onto a second line. The thresholds are the
      // text lengths that still fit one line at 4, 3 and 2 columns.
      const longest = Math.max(...values.map((v) => this.displayValue(v).length));
      const cols = longest <= 12 ? 4 : longest <= 17 ? 3 : longest <= 29 ? 2 : 1;
      const style = grid ? ` style="--bundle-cols:${cols}"` : '';

      return `<p class="bundle-tiers__label">
                <span class="bundle-tiers__name">${this.escape(this.optionLabel(name))}:&nbsp;${chosen}</span>${hint}
              </p>
              <div class="bundle-pills${grid}"${style}>${pills}</div>`;
    });

    this.optionsHost.innerHTML = groups.join('');
  }

  renderSummary() {
    const variant = this.variantFor(this.tier, this.selection);
    if (!variant) {
      this.summary.hidden = true;
      return;
    }

    const retail = this.retailFor(this.tier, this.selection);
    const retailEl = this.summary.querySelector('[data-sum-retail]');
    const retailRow = this.summary.querySelector('[data-sum-retail-row]');
    const saveBlock = this.summary.querySelector('[data-sum-save]');
    const nudge = this.summary.querySelector('[data-sum-nudge]');

    this.summary.hidden = false;
    this.summary.querySelector('[data-sum-total]').textContent = this.money(variant.price);

    if (retail && retail > variant.price) {
      const diff = retail - variant.price;
      retailEl.textContent = this.money(retail);
      // The row, not just the figure — an orphaned label read as a load error.
      if (retailRow) retailRow.hidden = false;
      this.summary.querySelector('[data-sum-save-value]').textContent =
        `${this.moneyShort(diff)} · ${Math.round((diff / retail) * 100)}% off`;
      saveBlock.hidden = false;
    } else {
      if (retailRow) retailRow.hidden = true;
      saveBlock.hidden = true;
    }

    // With no saving to state, the figures are just the price repeated — it is
    // already above the gallery and on the button. Drop to a bare nudge line.
    this.summary.classList.toggle('is-plain', saveBlock.hidden);

    // Upsell to the next tier up
    const order = this.data.tiers.slice().sort((a, b) => a.count - b.count);
    const next = order.find((t) => t.count > this.tier.count);

    if (!next) {
      nudge.textContent = 'Best price per bundle we offer.';
      return;
    }

    const nextOptions = this.projectSelection(this.tier, next);
    const nextVariant = this.variantFor(next, nextOptions);
    const nextRetail = this.retailFor(next, nextOptions);
    const currentSave = retail ? retail - variant.price : 0;

    if (nextVariant && nextRetail && nextRetail > nextVariant.price) {
      const extra = nextRetail - nextVariant.price - currentSave;
      if (extra > 0) {
        nudge.innerHTML =
          `<span class="bundle-summary__nudge-text">Add one more bundle and save ` +
          `<b>${this.moneyShort(extra)}</b> more.</span>` +
          `<button type="button" class="bundle-summary__nudge-btn">Make it ${next.count}</button>`;
        nudge.querySelector('button').addEventListener('click', (e) => {
          this.selection = nextOptions;
          this.tier = next;
          this.render();
          if (window.history) window.history.replaceState({}, '', next.url);
          e.currentTarget.blur();
        });
        return;
      }
    }

    nudge.textContent = '';
    this.collapseIfEmpty();
  }

  /**
   * Hide the whole panel when every line inside it is hidden or blank.
   * With no comparison price and no next tier to suggest, the panel stayed
   * visible with nothing in it and collapsed to its own border — a stray
   * hairline sitting just above Add to cart.
   */
  collapseIfEmpty() {
    const rows = this.summary.querySelectorAll('[data-sum-retail-row], [data-sum-save]');
    const anyRow = Array.from(rows).some((el) => !el.hidden);
    const nudge = this.summary.querySelector('[data-sum-nudge]');
    const anyNudge = nudge && nudge.textContent.trim().length > 0;

    // The total on its own is the price already shown above the gallery and on
    // the button, so it does not count as something worth showing a panel for.
    this.summary.hidden = !anyRow && !anyNudge;
  }

  /** Push the selected variant into the theme's product form */
  syncForm() {
    const form = document.querySelector(this.dataset.formSelector);
    if (!form) return;

    const variant = this.variantFor(this.tier, this.selection);
    const input = form.querySelector('input[name="id"]');
    if (input && variant) input.value = variant.id;

    const button = form.querySelector('[name="add"]');
    if (button) {
      const ok = variant && variant.available;
      button.disabled = !ok;
      // The label lives in its own span in some themes and is the button's own
      // text in others; a selector lets the host say which without guessing.
      const text =
        (this.dataset.addTextSelector && button.querySelector(this.dataset.addTextSelector)) ||
        button.querySelector('span') ||
        button;
      text.textContent = ok
        ? this.dataset.addLabel || 'Add to cart'
        : this.dataset.soldOutLabel || 'Sold out';
    }

    // Some layouts repeat the price inside the button
    if (this.dataset.addPriceSelector && variant) {
      document.querySelectorAll(this.dataset.addPriceSelector).forEach((el) => {
        el.textContent = this.money(variant.price);
      });
    }

    // Theme's own price display, when it renders one separately
    const priceEl = document.querySelector(this.dataset.priceSelector);
    if (priceEl && variant) {
      const target = priceEl.querySelector('.price-item--regular') || priceEl;
      target.textContent = this.money(variant.price);
    }
  }

  /** Tiers share one photo set, so only the lead image changes */
  syncImage() {
    if (!this.tier.image) return;
    const selector =
      this.dataset.imageSelector || '.product__media img, [data-product-featured-image]';
    const media = document.querySelector(selector);
    if (!media || media.src === this.tier.image) return;

    media.src = this.tier.image;
    media.removeAttribute('srcset');

    // The lightbox does not read src — it reads the full-size address stored
    // alongside it, which still pointed at the listing the page was rendered
    // for. Switching to two bundles then zooming showed the one-bundle photo.
    if (media.hasAttribute('data-hqpdp-zoom-src')) {
      // The serialised tier image is a 1400px CDN url; ask the same file for
      // the width the lightbox uses rather than blowing up a thumbnail.
      const zoom = this.tier.image.replace(/([?&]width=)\d+/, '$13000');
      media.setAttribute('data-hqpdp-zoom-src', zoom);
      // A tier of its own has no hover video attached to this frame
      media.removeAttribute('data-hqpdp-zoom-video');
      media.removeAttribute('data-hqpdp-zoom-video-type');
    }
  }

  /* ---------- helpers ---------- */

  money(cents) {
    const amount = (cents / 100).toFixed(2);
    const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return this.moneyFormat
      .replace('{{amount}}', withCommas)
      .replace('{{amount_no_decimals}}', withCommas.split('.')[0])
      .replace('{{amount_with_comma_separator}}', withCommas)
      .replace(/\{\{\s*\w+\s*\}\}/g, withCommas);
  }

  /**
   * Money with a whole-cent amount written without the decimals — "+$25"
   * rather than "+$25.00". Used for the price deltas on the option pills,
   * matching how the native pills on the other product templates read.
   * Amounts with real cents keep them.
   */
  moneyShort(cents) {
    return this.money(cents).replace(/\.00\b/, '');
  }

  escape(str) {
    return String(str).replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
  }
}

if (!customElements.get('bundle-tiers')) {
  customElements.define('bundle-tiers', BundleTiers);
}